import {useCallback, useEffect, useRef, useState} from 'react';

import {useCultureAgentStream} from '../hooks/useCultureAgentStream';
import {ChatMessage, ChatStreamMeta, CultureAgentConfig, CultureAgentError, CultureAgentLabels} from '../types';

import {ChatHeader} from './ChatHeader';
import {ChatInputSection} from './ChatInputSection';
import {ChatMessages} from './ChatMessages';
import {ChatProgress} from './ChatProgress';
import {SurveyReadinessInput} from './SurveyReadinessInput';

import './CultureAgent.css';

export interface CultureAgentProps {
  config: CultureAgentConfig;
  labels: CultureAgentLabels;
  language?: string;
  onSessionObtained?: (sessionId: string) => void;
  onSurveyComplete: (sessionId: string, certainty: number) => void;
  onError?: (error: CultureAgentError) => void;
}

export const CultureAgent = ({
  config,
  labels,
  language = 'de',
  onSessionObtained,
  onSurveyComplete,
  onError,
}: CultureAgentProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [chatMeta, setChatMeta] = useState<ChatStreamMeta>({
    certainty: null,
    done: false,
  });
  const [isConnected, setIsConnected] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [awaitingUserStart, setAwaitingUserStart] = useState(false);
  const [userDeclined, setUserDeclined] = useState(false);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);

  const {obtainSession, streamMessage, resetSurvey, streaming, error, sessionId} = useCultureAgentStream(config);

  const replaceLastBotMessage = (updater: (prev: string) => string) => {
    setMessages((prev) => {
      const updated = [...prev];
      const last = updated.at(-1);
      if (last?.role === 'bot') {
        updated[updated.length - 1] = {...last, content: updater(last.content), type: 'text'};
      }
      return updated;
    });
  };

  const appendThinkingMessage = () => {
    setMessages((prev) => [...prev, {role: 'bot', content: '', type: 'thinking'}]);
  };

  const updateChatMeta = (result: ChatStreamMeta) => {
    setChatMeta((prev) => ({
      certainty: result.certainty ?? prev.certainty,
      done: result.done,
    }));
  };

  const initialize = useCallback(async () => {
    if (hasInitialized.current || isInitializing) return;

    setIsInitializing(true);
    hasInitialized.current = true;

    try {
      const sid = await obtainSession();
      setIsConnected(true);
      onSessionObtained?.(sid);
      await resetSurvey();

      setMessages([{role: 'bot', content: labels.initialMessage, type: 'text'}]);
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setMessages((prev) => [...prev, {role: 'bot', content: '', type: 'thinking'}]);
      await new Promise((resolve) => setTimeout(resolve, 2000));

      setMessages((prev) => [...prev.slice(0, -1), {role: 'bot', content: labels.instructions, type: 'instruction'}]);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setMessages((prev) => [...prev, {role: 'bot', content: labels.readinessMessage, type: 'text'}]);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setAwaitingUserStart(true);
    } finally {
      setIsInitializing(false);
    }
  }, [
    obtainSession,
    resetSurvey,
    labels.initialMessage,
    labels.instructions,
    labels.readinessMessage,
    isInitializing,
    onSessionObtained,
  ]);

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (error && onError) onError(error);
  }, [error, onError]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const localeYes = language === 'de' ? 'ja' : 'yes';
  const localeNo = language === 'de' ? 'nein' : 'no';

  const handleReadinessYes = useCallback(async () => {
    if (streaming || isInitializing) return;
    setMessages((prev) => [...prev, {role: 'user', content: localeYes, type: 'text'}]);
    setAwaitingUserStart(false);
    appendThinkingMessage();
    let fullMessage = '';
    const result = await streamMessage(
      localeYes,
      (token) => {
        fullMessage += token;
        replaceLastBotMessage(() => fullMessage);
      },
      language
    );
    if (result) updateChatMeta(result);
  }, [streaming, isInitializing, localeYes, streamMessage, language]);

  const handleReadinessNo = useCallback(() => {
    setMessages((prev) => [
      ...prev,
      {role: 'user', content: localeNo, type: 'text'},
      {role: 'bot', content: labels.notReadyMessage, type: 'text'},
    ]);
    setAwaitingUserStart(false);
    setUserDeclined(true);
  }, [localeNo, labels.notReadyMessage]);

  const handleSend = useCallback(
    async (type: 'text') => {
      if (streaming || isInitializing) return;

      const content = input.trim();
      if (!content) return;

      setMessages((prev) => [...prev, {role: 'user', content, type}]);
      setInput('');
      appendThinkingMessage();

      let fullMessage = '';
      const result = await streamMessage(
        content,
        (token) => {
          fullMessage += token;
          replaceLastBotMessage(() => fullMessage);
        },
        language
      );

      if (result) updateChatMeta(result);
    },
    [input, streaming, isInitializing, streamMessage, language]
  );

  const handleSeeResults = () => {
    if (sessionId && chatMeta.certainty !== null) {
      onSurveyComplete(sessionId, chatMeta.certainty);
    }
  };

  return (
    <div className='culture-agent'>
      <ChatHeader
        agentName={labels.agentName}
        agentTitle={labels.agentTitle}
        statusLabel={labels.agentStatusLabel}
        isActive={isConnected}
      />
      <ChatProgress
        certainty={chatMeta.certainty}
        done={chatMeta.done}
        passThreshold={config.passThreshold}
        seeResultsLabel={labels.seeResults}
        resultsHint={labels.resultsHint}
        progressDescription={labels.progressDescription}
        onSeeResults={handleSeeResults}
      />
      <div className='culture-agent__messages' ref={messagesContainerRef}>
        <ChatMessages messages={messages} chatEndRef={chatEndRef} agentName={labels.agentName} />
      </div>
      {awaitingUserStart && (
        <SurveyReadinessInput
          yesLabel={labels.readinessYesLabel}
          noLabel={labels.readinessNoLabel}
          onYes={handleReadinessYes}
          onNo={handleReadinessNo}
          disabled={streaming}
        />
      )}
      {userDeclined && (
        <div className="culture-agent__refresh-hint">{labels.refreshToRestartHint}</div>
      )}
      {!awaitingUserStart && !userDeclined && (
        <ChatInputSection
          input={input}
          loading={streaming || isInitializing}
          done={chatMeta.done}
          inputPlaceholder={labels.inputPlaceholder}
          onChange={handleInputChange}
          onSend={handleSend}
        />
      )}
    </div>
  );
};
